
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { buildPermissions } from '../utils/rbac';

const OrgContext = createContext({
  profile: null,
  organization: null,
  permissions: buildPermissions(),
  loading: false,
  error: null,
  refresh: async () => {},
});

export function OrgProvider({ children }) {
  const { session } = useAuth();
  const userId = session?.user?.id || null;
  const [profile, setProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [permissions, setPermissions] = useState(buildPermissions());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetState = useCallback(() => {
    setProfile(null);
    setOrganization(null);
    setPermissions(buildPermissions());
    setError(null);
  }, []);

  const fetchSnapshot = useCallback(
    async (force = false) => {
      if (!userId) {
        resetState();
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.post('/org/bootstrap');
        setProfile(data?.profile ?? null);
        setOrganization(data?.organization ?? null);
        setPermissions(data?.permissions ?? buildPermissions());
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || 'Unable to load organization context.';
        setError(message);
        if (force) {
          setProfile(null);
          setOrganization(null);
          setPermissions(buildPermissions());
        }
      } finally {
        setLoading(false);
      }
    },
    [userId, resetState]
  );

  useEffect(() => {
    if (!userId) {
      resetState();
      return;
    }
    fetchSnapshot(true);
  }, [userId, fetchSnapshot, resetState]);

  const refresh = useCallback(async () => {
    await fetchSnapshot(true);
  }, [fetchSnapshot]);

  const value = useMemo(
    () => ({
      profile,
      organization,
      permissions,
      loading,
      error,
      refresh,
    }),
    [profile, organization, permissions, loading, error, refresh]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  return useContext(OrgContext);
}
